
import { useCallback, useEffect, useRef } from "react"

export function useEventListener<K extends keyof WindowEventMap>(
  type: K,
  element: Window,
  handler: (event: WindowEventMap[K]) => void
): void

export function useEventListener<K extends keyof HTMLElementEventMap>(
  type: K,
  element: HTMLElement | null,
  handler: (event: HTMLElementEventMap[K]) => void
): void

export function useEventListener<K extends (keyof WindowEventMap | keyof HTMLElementEventMap)>(
  type: K,
  element: Window | HTMLElement | null,
  handler: (event: K extends keyof WindowEventMap ? WindowEventMap[K] : K extends keyof ElementEventMap ? HTMLElementEventMap[K] : never) => void
): void {
  // I store the handler in a ref because it's likely to be recreated on every render, but I don't want to remove and re-add the event listener on every render. I arrange for the event listener to always call the handler through this ref, and I update the ref on every render, so the event listener always calls the current handler. That means I don't need the handler's deps.
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const wrapper = useCallback((event: any) => {
    handlerRef.current(event)
  }, [handlerRef])

  useEffect(() => {
    if (!element) {
      return
    }
    element.addEventListener(type, wrapper)
    return () => {
      element.removeEventListener(type, wrapper)
    }
  }, [type, wrapper, element])
}
